import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { BET_TABS } from 'crm-events-components/internal/BET/timeline/constants/BETTimelineConstants';
import { canRenderBETContactSummaryTab, canRenderBETCustomerSummaryTab, canRenderBETRenewalSummaryTab, canRenderBETCallSummaryTab } from 'crm-events-components/internal/BET/permissions/helpers/BETTimelinePermissionsUtils';
import PropTypes from 'prop-types';
import { AnyCrmObjectTypePropType, AnyCrmObjectPropType
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'cust... Remove this comment to see the full error message
} from 'customer-data-objects-ui-components/propTypes/CrmObjectTypes';
import { ThreadEmailRepliesContext } from 'conversations-thread-view-crm-events/email/context/ThreadEmailRepliesContext';
import TimelineWrapper from './TimelineWrapper';
import I18n from 'I18n';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useLegacyQueryParams, useSelectedTab, useTimelineActionHandlerContext, TRIGGER_RHUMB_SUCCESS, TimelineStatusStore, ENGAGEMENTS_ACTIVITY, ENGAGEMENTS_EMAIL, ENGAGEMENTS_MEETINGS } from 'crm-events-data';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'gene... Remove this comment to see the full error message
import { useStoreDependency } from 'general-store';
import { useTimelineEventsData } from './useTimelineEventsData';
import TimelineControlsWrapper from './TimelineControlsWrapper';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// we need this to be interactable but invisible to sighted users
// meaning that UIScreenReaderOnly is not sufficient
const InvisibleButton = styled.button.withConfig({
  displayName: "TimelineContainerVirtualized__InvisibleButton",
  componentId: "drn8k5-0"
})(["opacity:0;height:0;width:0;pointer-events:none;overflow:hidden;"]);
const InvisibleNav = styled.nav.withConfig({
  displayName: "TimelineContainerVirtualized__InvisibleNav",
  componentId: "drn8k5-1"
})(["height:0;width:0;overflow:hidden;"]);
const propTypes = {
  forceActivityTabForBET: PropTypes.bool,
  objectType: AnyCrmObjectTypePropType.isRequired,
  scopes: PropTypes.arrayOf(PropTypes.string),
  subjectId: PropTypes.string.isRequired,
  subject: AnyCrmObjectPropType
};
const defaultProps = {
  scopes: []
};
export const dependencies = {
  hasMoreEvents: {
    stores: [TimelineStatusStore],
    deref: () => {
      const timelineStatus = TimelineStatusStore.get('timelineStatus');
      if (!timelineStatus) {
        return false;
      }
      return timelineStatus.get('hasMore');
    }
  }
};
const TimelineContainerVirtualized = ({
  forceActivityTabForBET,
  objectType,
  scopes,
  subjectId,
  subject
}) => {
  const [hasTriggeredBETRhumbSuccess, setHasTriggeredBETRhumbSuccess] = useState(false);
  const [shouldThreadEmailReplies, setShouldThreadEmailReplies] = useState(false);
  const {
    selectedTab: selected
  } = useSelectedTab();
  const queryParams = useLegacyQueryParams();
  const searchParamsFromUrl = queryParams.defaultTab || '';
  const profileStartTime = useRef(performance.now());
  const handleTimelineAction = useTimelineActionHandlerContext();
  const events = useTimelineEventsData({
    objectType,
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string[] & never[]' is not assignable to typ... Remove this comment to see the full error message
    scopes,
    subjectId,
    subject
  });
  const hasMoreEvents = useStoreDependency(dependencies.hasMoreEvents);
  const betTabIsSelected = useMemo(() => {
    // Selected represents a tab that was manually changed. This is undefined until user interaction
    if (selected && !BET_TABS.includes(selected)) {
      return false;
    }
    if (forceActivityTabForBET && searchParamsFromUrl !== 'shared') {
      return false;
    }
    if (searchParamsFromUrl === 'shared' || canRenderBETContactSummaryTab({
      objectType,
      scopes,
      subject
    }) || canRenderBETRenewalSummaryTab({
      objectType,
      scopes,
      subject
    }) || canRenderBETCustomerSummaryTab({
      objectType,
      scopes,
      subject
    }) || canRenderBETCallSummaryTab({
      objectType,
      scopes,
      subject
    })) {
      return true;
    }
    return false;
  }, [selected, forceActivityTabForBET, searchParamsFromUrl, objectType, scopes, subject]);
  const handleToggleShouldThreadEmailReplies = useCallback(() => {
    setShouldThreadEmailReplies(!shouldThreadEmailReplies);
  }, [shouldThreadEmailReplies, setShouldThreadEmailReplies]);
  const handleTriggerRhumbSuccess = useCallback(() => {
    handleTimelineAction(TRIGGER_RHUMB_SUCCESS);
    setHasTriggeredBETRhumbSuccess(true);
  }, [handleTimelineAction, setHasTriggeredBETRhumbSuccess]);
  const invisibleNav = useMemo(() => {
    if (!hasMoreEvents) {
      return null;
    }
    return /*#__PURE__*/_jsx(InvisibleNav, {
      role: "navigation",
      "aria-label": I18n.text('timeline.accessibility.infiniteScroll'),
      children: /*#__PURE__*/_jsx(InvisibleButton, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "timeline.accessibility.infiniteScroll"
        })
      })
    });
  }, [hasMoreEvents]);
  const selectedTab = selected || ENGAGEMENTS_ACTIVITY;
  useEffect(() => {
    if (betTabIsSelected && !hasTriggeredBETRhumbSuccess) {
      handleTriggerRhumbSuccess();
    }
  }, [betTabIsSelected, handleTriggerRhumbSuccess, hasTriggeredBETRhumbSuccess]);
  return /*#__PURE__*/_jsxs(View, {
    "data-measured-element": "timeline-container",
    role: "feed",
    "aria-live": "polite",
    paddingBlockEnd: "400",
    children: [/*#__PURE__*/_jsxs(ThreadEmailRepliesContext.Provider, {
      value: {
        shouldThreadEmailReplies,
        toggleShouldThreadEmailReplies: handleToggleShouldThreadEmailReplies
      },
      children: [/*#__PURE__*/_jsx(TimelineControlsWrapper, {
        forceActivityTabForBET: forceActivityTabForBET
      }), !betTabIsSelected && /*#__PURE__*/_jsx(TimelineWrapper, {
        activityTabIsSelected: selectedTab === ENGAGEMENTS_ACTIVITY,
        emailTabIsSelected: selectedTab === ENGAGEMENTS_EMAIL,
        meetingsTabIsSelected: selectedTab === ENGAGEMENTS_MEETINGS,
        events: events,
        objectType: objectType,
        profileStartTime: profileStartTime.current,
        selectedTab: selectedTab,
        subjectId: subjectId
      })]
    }), invisibleNav]
  });
};
TimelineContainerVirtualized.propTypes = propTypes;
/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
TimelineContainerVirtualized.defaultProps = defaultProps;
export default TimelineContainerVirtualized;